import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentUserQuery, useLogoutMutation } from '@/entities/user';
import { useInvalidateDecedentQueries } from '@/entities/decedent';
import useLogoutIntentStore from '@/common/stores/logout-intent';

const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const { pathname } = useLocation();
  const pendingLogoutPath = useLogoutIntentStore((state) => state.pendingPath);
  const clearLogout = useLogoutIntentStore((state) => state.clearLogout);
  const currentUserQuery = useCurrentUserQuery();
  const { data: user, isError, isFetched, isPending, refetch } = currentUserQuery;
  const { resetDecedentQueries } = useInvalidateDecedentQueries();
  const logoutMutation = useLogoutMutation({
    onSuccess: async () => {
      resetDecedentQueries();
    },
  });

  const refreshUser = useCallback(async () => {
    const { data } = await refetch();
    return data;
  }, [refetch]);

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  useEffect(() => {
    if (!pendingLogoutPath || pathname !== pendingLogoutPath) {
      return;
    }

    clearLogout();
    logout();
  }, [clearLogout, logout, pathname, pendingLogoutPath]);

  const value = useMemo(
    () => ({
      user: user || null,
      isAuth: !!user,
      isAuthLoading: isPending,
      isAuthResolved: isFetched || isError,
      refreshUser,
      logout,
      isLoggingOut: logoutMutation.isPending,
    }),
    [
      isError,
      isFetched,
      isPending,
      logout,
      logoutMutation.isPending,
      refreshUser,
      user,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
};

export default AuthProvider;
